import React, { useEffect, useState } from 'react';
import { Loader2, Upload, Download, RefreshCcw, CheckCircle2, AlertTriangle, Play, Cloud, FileSpreadsheet, PlayCircle } from 'lucide-react';
import { adminApi, resolveImage } from '../../api';

export default function AdminImport() {
  const [jobs, setJobs] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvApplyResult, setCsvApplyResult] = useState(null);
  const [csvJobProgress, setCsvJobProgress] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvSchema, setCsvSchema] = useState(null);
  const [showSchema, setShowSchema] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [wixFiles, setWixFiles] = useState([]);
  const [wixResult, setWixResult] = useState(null);
  const [wixBusy, setWixBusy] = useState(false);
  const [geoJob, setGeoJob] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [tab, setTab] = useState('wix-csv');

  // Poll geocode job
  useEffect(() => {
    if (!geoJob?.job_id || geoJob.status === 'completed' || geoJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const j = await adminApi.geocodeStatus(geoJob.job_id);
        setGeoJob({ ...j, job_id: geoJob.job_id });
        if (['completed', 'failed'].includes(j.status)) clearInterval(t);
      } catch (e) { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [geoJob?.status, geoJob?.job_id]);

  const runGeocode = async () => {
    setGeoBusy(true); setGeoJob(null);
    try {
      const r = await adminApi.geocodeRun(200);
      if (r.queued === 0) {
        alert(r.message || 'Tutte le attività hanno già coordinate');
      } else {
        setGeoJob({ ...r, status: 'scanning' });
      }
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setGeoBusy(false); }
  };

  const loadJobs = async () => {
    try { const r = await adminApi.importJobs(); setJobs(r.jobs || []); } catch (e) {}
  };

  useEffect(() => { loadJobs(); }, []);

  // Poll active job
  useEffect(() => {
    if (!currentJob || !['scanning', 'pending'].includes(currentJob.status)) return;
    const t = setInterval(async () => {
      try {
        const j = await adminApi.wixStatus(currentJob.job_id || currentJob.id);
        setCurrentJob(j);
        if (['previewed', 'completed', 'failed'].includes(j.status)) {
          clearInterval(t);
          loadJobs();
        }
      } catch (e) { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [currentJob?.status]);

  const runWixScan = async () => {
    setScanning(true);
    try {
      const r = await adminApi.wixScan();
      setCurrentJob({ ...r, status: 'scanning', pages_scanned: 0, activities_found: 0 });
    } catch (e) {
      alert('Errore avvio scan: ' + (e.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  const applyWix = async () => {
    if (!currentJob) return;
    if (!window.confirm(`Applicare ${currentJob.activities_new} nuove + ${currentJob.activities_updated} aggiornate al database?`)) return;
    setApplying(true);
    try {
      const r = await adminApi.wixApply(currentJob.job_id || currentJob.id);
      alert(`Import applicato: ${r.created} create, ${r.updated} aggiornate`);
      setCurrentJob({ ...currentJob, status: 'completed' });
      loadJobs();
    } catch (e) {
      alert('Errore apply: ' + (e.response?.data?.detail || e.message));
    } finally { setApplying(false); }
  };

  const downloadTemplate = async () => {
    const r = await adminApi.csvTemplate();
    setCsvSchema(r);
    const blob = new Blob([r.csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = r.filename;
    a.click();
  };

  const loadSchema = async () => {
    if (csvSchema) { setShowSchema(v => !v); return; }
    try {
      const r = await adminApi.csvTemplate();
      setCsvSchema(r);
      setShowSchema(true);
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const previewCsv = async () => {
    if (!csvFile) return;
    setCsvBusy(true); setCsvPreview(null); setCsvApplyResult(null);
    try {
      const r = await adminApi.csvPreview(csvFile);
      setCsvPreview(r);
    } catch (e) { alert('Errore preview: ' + (e.response?.data?.detail || e.message)); }
    finally { setCsvBusy(false); }
  };

  const applyCsv = async () => {
    if (!csvFile) return;
    if (!window.confirm(`Importare ${csvPreview?.total || '?'} righe nel database?`)) return;
    setCsvBusy(true);
    setCsvApplyResult(null);
    setCsvJobProgress({ uploading: true, uploadPct: 0, processedRows: 0, totalRows: 0, status: 'uploading' });
    try {
      const { job_id } = await adminApi.csvApplyAsync(csvFile, (pct) => {
        setCsvJobProgress(p => ({ ...p, uploadPct: pct }));
      });
      // Poll status
      setCsvJobProgress(p => ({ ...p, uploading: false, status: 'queued' }));
      let done = false;
      while (!done) {
        await new Promise(r => setTimeout(r, 500));
        const job = await adminApi.csvJobStatus(job_id);
        setCsvJobProgress({
          uploading: false, uploadPct: 100,
          processedRows: job.processed_rows || 0,
          totalRows: job.total_rows || 0,
          created: job.activities_new || 0,
          updated: job.activities_updated || 0,
          skipped: job.skipped_rows || 0,
          errors: job.errors || [],
          status: job.status,
        });
        if (['completed', 'failed'].includes(job.status)) {
          done = true;
          setCsvApplyResult({
            ok: job.status === 'completed',
            created: job.activities_new || 0,
            updated: job.activities_updated || 0,
            skipped: job.skipped_rows || 0,
            errors: job.errors || [],
          });
        }
      }
    } catch (e) { alert('Errore apply: ' + (e.response?.data?.detail || e.message)); }
    finally { setCsvBusy(false); }
  };

  const exportCsv = async () => {
    const r = await adminApi.csvExport();
    const blob = new Blob([r.csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `yup_activities_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="admin-import-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Importazione contenuti</h1>
        <p className="text-sm text-gray-600 mt-1">Migra contenuti dal sito Wix esistente o carica un CSV. Gli import sono idempotenti (basati su import_hash).</p>
      </div>

      {/* Geocoding quick action */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-gray-800 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-600 text-white text-xs">📍</span>
            Geolocalizzazione automatica
          </div>
          <div className="text-xs text-gray-600 mt-0.5">
            Ricava le coordinate mancanti dagli indirizzi via OpenStreetMap (Nominatim, gratis, ~1s per attività).
          </div>
          {geoJob && (
            <div className="mt-2 text-xs">
              <span className={`inline-block px-2 py-0.5 rounded font-medium ${geoJob.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700 animate-pulse'}`}>
                {geoJob.status}
              </span>
              {' '}Coordinate assegnate: <strong>{geoJob.activities_updated || geoJob.activities_found || 0}</strong>
              {geoJob.errors?.length > 0 && <span className="text-amber-700"> · falliti: {geoJob.errors[0]}</span>}
            </div>
          )}
        </div>
        <button
          onClick={runGeocode}
          disabled={geoBusy || (geoJob && geoJob.status === 'scanning')}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="geocode-run-btn"
        >
          {(geoBusy || geoJob?.status === 'scanning') ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
          Geolocalizza attività
        </button>
      </div>

      <div className="flex gap-2 border-b border-gray-200 flex-wrap">
        <button
          onClick={() => setTab('wix-csv')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'wix-csv' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-wixcsv-btn"
        >
          <FileSpreadsheet size={16} className="inline mr-2" />Export Wix (CSV multi-file)
        </button>
        <button
          onClick={() => setTab('wix')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'wix' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-wix-btn"
        >
          <Cloud size={16} className="inline mr-2" />Crawler Wix pubblico
        </button>
        <button
          onClick={() => setTab('csv')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'csv' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-csv-btn"
        >
          <FileSpreadsheet size={16} className="inline mr-2" />CSV Template YUP
        </button>
        <button
          onClick={() => setTab('jobs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'jobs' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-jobs-btn"
        >
          <RefreshCcw size={16} className="inline mr-2" />Job precedenti
        </button>
      </div>

      {/* ---------- WIX EXPORT CSV MULTI ---------- */}
      {tab === 'wix-csv' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800">Carica export Wix (uno o più file CSV)</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              Esporta le tue collezioni dal CMS Wix e caricale qui insieme. Il sistema riconosce automaticamente le colonne <code>nome</code>, <code>indirizzo</code>, <code>telefono</code>, <code>sito</code>, <code>Immagine</code> (formato <code>wix:image://</code>) e <code>posizione</code>. La categoria viene dedotta dal nome file o dall'intestazione <code>(Item)</code>. Puoi ricaricare più volte: le attività sono idempotenti (dedup via nome + indirizzo).
            </p>

            <div className="mt-4 border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
              <input
                type="file" multiple accept=".csv"
                onChange={e => { setWixFiles(Array.from(e.target.files || [])); setWixResult(null); }}
                className="hidden" id="wix-input" data-testid="wixcsv-file-input"
              />
              <label htmlFor="wix-input" className="cursor-pointer">
                <Upload size={28} className="mx-auto text-gray-400 mb-2" />
                <div className="text-sm text-gray-700 font-medium">
                  {wixFiles.length > 0 ? `${wixFiles.length} file selezionati` : 'Clicca per selezionare uno o più CSV Wix'}
                </div>
                <div className="text-xs text-gray-500 mt-1">Supporta anche upload multiplo</div>
              </label>
              {wixFiles.length > 0 && (
                <ul className="mt-3 text-xs text-gray-600 space-y-1 max-h-40 overflow-auto text-left">
                  {wixFiles.map((f, i) => <li key={i}>{f.name} ({(f.size / 1024).toFixed(1)} kB)</li>)}
                </ul>
              )}
            </div>

            {wixFiles.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={async () => {
                    setWixBusy(true); setWixResult(null);
                    try {
                      const r = await adminApi.wixCsvApply(wixFiles);
                      setWixResult(r);
                    } catch (e) {
                      alert('Errore: ' + (e.response?.data?.detail || e.message));
                    } finally { setWixBusy(false); }
                  }}
                  disabled={wixBusy}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="wixcsv-apply-btn"
                >
                  {wixBusy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  Importa nel database
                </button>
              </div>
            )}

            {wixResult && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
                <div className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle2 size={18} /> Import completato
                </div>
                <div className="text-sm text-green-700 mt-1">
                  Totale: <strong>{wixResult.created}</strong> create · <strong>{wixResult.updated}</strong> aggiornate
                </div>
                <table className="w-full text-sm mt-3">
                  <thead className="bg-white text-xs text-gray-500 uppercase">
                    <tr><th className="text-left px-3 py-2">File</th><th className="text-left px-3 py-2">Record</th><th className="text-left px-3 py-2">Create</th><th className="text-left px-3 py-2">Aggiornate</th><th className="text-left px-3 py-2">Note</th></tr>
                  </thead>
                  <tbody>
                    {wixResult.files.map((f, i) => (
                      <tr key={i} className="border-t border-green-100">
                        <td className="px-3 py-2">{f.filename}</td>
                        <td className="px-3 py-2">{f.records}</td>
                        <td className="px-3 py-2 text-green-700 font-medium">{f.created}</td>
                        <td className="px-3 py-2 text-blue-700 font-medium">{f.updated}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{f.empty ? 'Vuoto (solo header)' : f.error || 'OK'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}


      {/* ---------- WIX ---------- */}
      {tab === 'wix' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Crawler pubblico Wix</h2>
                <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                  Scansiona le pagine pubbliche di <code>yup-yuorutilitypoints.com</code> (categorie e sottocategorie), estrae le attività trovate, scarica le immagini nello storage locale e produce un'anteprima. Nessuna scrittura al DB finché non premi "Applica".
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  ⚠️ Nota: Wix carica i contenuti via JavaScript. Il crawler usa un browser headless per estrarre più dati possibili, ma alcuni elenchi lunghi richiedono scroll manuali. Per un import 100% completo usa la scheda "CSV / Excel" con l'export ufficiale Wix.
                </p>
              </div>
              <button
                onClick={runWixScan}
                disabled={scanning || currentJob?.status === 'scanning'}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="wix-scan-btn"
              >
                {scanning || currentJob?.status === 'scanning' ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                Avvia nuovo scan
              </button>
            </div>
          </div>

          {currentJob && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-gray-500">Job ID: <code>{currentJob.job_id || currentJob.id}</code></div>
                  <div className="text-lg font-semibold flex items-center gap-2">
                    Stato:
                    <StatusBadge status={currentJob.status} />
                  </div>
                </div>
                {currentJob.status === 'previewed' && (
                  <button onClick={applyWix} disabled={applying}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="wix-apply-btn">
                    {applying ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    Applica al database
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                <Stat label="Pagine" val={currentJob.pages_scanned} />
                <Stat label="Trovate" val={currentJob.activities_found} />
                <Stat label="Nuove" val={currentJob.activities_new} color="text-green-600" />
                <Stat label="Aggiornate" val={currentJob.activities_updated} color="text-blue-600" />
                <Stat label="Duplicate" val={currentJob.activities_duplicate} color="text-gray-400" />
                <Stat label="Immagini" val={currentJob.images_downloaded} color="text-purple-600" />
              </div>

              {currentJob.errors?.length > 0 && (
                <details className="mb-4">
                  <summary className="text-sm text-amber-700 cursor-pointer">{currentJob.errors.length} errori</summary>
                  <ul className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-3 space-y-1 max-h-40 overflow-auto">
                    {currentJob.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}

              {currentJob.preview?.length > 0 && (
                <div className="border border-gray-200 rounded overflow-hidden">
                  <div className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-200">Anteprima attività ({currentJob.preview.length})</div>
                  <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Nome</th>
                          <th className="text-left px-3 py-2">Categoria</th>
                          <th className="text-left px-3 py-2">Indirizzo</th>
                          <th className="text-left px-3 py-2">Telefono</th>
                          <th className="text-left px-3 py-2">Azione</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentJob.preview.slice(0, 200).map((p, i) => (
                          <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800 flex items-center gap-2">
                              {(p.image_local || p.image) && (
                                <img src={resolveImage(p.image_local || p.image)} alt="" className="w-8 h-8 object-cover rounded" />
                              )}
                              <span className="truncate max-w-xs">{p.name}</span>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{p.category_key}</td>
                            <td className="px-3 py-2 text-gray-600 truncate max-w-xs">{p.address || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{p.phone || '—'}</td>
                            <td className="px-3 py-2">
                              <ActionPill action={p._action} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------- CSV ---------- */}
      {tab === 'csv' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800">Import / Export CSV</h2>
            <p className="text-sm text-gray-600 mt-1">
              Formato: UTF-8, delimitatore <code>,</code> o <code>;</code> (auto-rilevato). I nomi colonne
              possono essere in italiano (es. <code>nome</code>, <code>citta</code>, <code>categoria</code>).
              Le righe sono idempotenti: se esiste un import_hash coincidente, l'attività viene aggiornata invece che duplicata.
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={downloadTemplate}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md text-sm font-medium inline-flex items-center gap-2"
                data-testid="csv-template-btn">
                <Download size={16} /> Scarica template
              </button>
              <button onClick={exportCsv}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md text-sm font-medium inline-flex items-center gap-2"
                data-testid="csv-export-btn">
                <Download size={16} /> Esporta attività attuali
              </button>
              <button onClick={loadSchema}
                className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md text-sm font-medium inline-flex items-center gap-2"
                data-testid="csv-schema-btn">
                <FileSpreadsheet size={16} /> {showSchema ? 'Nascondi' : 'Mostra'} guida colonne
              </button>
              <button onClick={() => setShowTutorial(v => !v)}
                className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-md text-sm font-medium inline-flex items-center gap-2"
                data-testid="csv-tutorial-btn">
                <PlayCircle size={16} /> {showTutorial ? 'Nascondi' : 'Mostra'} tutorial Excel
              </button>
            </div>

            {showTutorial && (
              <div className="mt-5 border border-emerald-300 rounded-lg overflow-hidden" data-testid="csv-tutorial-panel">
                <div className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-bold flex items-center gap-2">
                  <PlayCircle size={18} /> Tutorial: come preparare il CSV partendo da Excel
                </div>
                <div className="p-5 space-y-5 text-sm bg-emerald-50/40">

                  {/* STEP 1 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">1</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Scarica il template</div>
                      <div className="text-gray-600 text-xs mt-1">Clicca "Scarica template" qui sopra. Otterrai un file <code>yup_activities_template.csv</code> con le colonne corrette già pronte.</div>
                      <div className="mt-2 bg-white border border-gray-200 rounded p-2 text-xs font-mono text-gray-700 overflow-x-auto">
                        name,city_slug,category_key,subcategory,description_it,phone,address,website,...
                      </div>
                    </div>
                  </div>

                  {/* STEP 2 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">2</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Apri il file con Excel (o Google Fogli)</div>
                      <div className="text-gray-600 text-xs mt-1">Doppio clic sul file. Excel apre una tabella con la prima riga di intestazioni. Compila una riga per attività.</div>
                      <div className="mt-2 bg-white border border-gray-200 rounded overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100 text-gray-600">
                            <tr>
                              <th className="px-2 py-1 text-left">name</th>
                              <th className="px-2 py-1 text-left">city_slug</th>
                              <th className="px-2 py-1 text-left">category_key</th>
                              <th className="px-2 py-1 text-left">address</th>
                              <th className="px-2 py-1 text-left">phone</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t border-gray-100">
                              <td className="px-2 py-1">La Rucola</td>
                              <td className="px-2 py-1 text-emerald-700">desenzano</td>
                              <td className="px-2 py-1 text-emerald-700">ristoranti</td>
                              <td className="px-2 py-1 text-gray-500">Via San Zeno 5</td>
                              <td className="px-2 py-1 text-gray-500">+3903099911</td>
                            </tr>
                            <tr className="border-t border-gray-100">
                              <td className="px-2 py-1">Bar Alpino</td>
                              <td className="px-2 py-1 text-emerald-700">sirmione</td>
                              <td className="px-2 py-1 text-emerald-700">ristoranti</td>
                              <td className="px-2 py-1 text-gray-500">Piazza Carducci 12</td>
                              <td className="px-2 py-1 text-gray-500">+39030991</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* STEP 3 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">3</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Usa <span className="text-emerald-700">solo</span> valori validi per city_slug e category_key</div>
                      <div className="text-gray-600 text-xs mt-1">Clicca "Mostra guida colonne" qui sopra per vedere la lista completa. In sintesi:</div>
                      <div className="mt-2 flex flex-wrap gap-1.5 items-center text-xs">
                        <span className="text-gray-500">Città:</span>
                        <code className="px-1.5 py-0.5 bg-white border border-emerald-200 rounded text-emerald-800">desenzano</code>
                        <code className="px-1.5 py-0.5 bg-white border border-emerald-200 rounded text-emerald-800">sirmione</code>
                        <code className="px-1.5 py-0.5 bg-white border border-emerald-200 rounded text-emerald-800">peschiera</code>
                      </div>
                      <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        ⚠️ NIENTE MAIUSCOLE, NIENTE ACCENTI, NIENTE SPAZI. "Città" ❌ → "desenzano" ✅
                      </div>
                    </div>
                  </div>

                  {/* STEP 4 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">4</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Salva come CSV UTF-8</div>
                      <div className="text-gray-600 text-xs mt-1">In Excel: <strong>File → Salva con nome → CSV UTF-8 (delimitato dalla virgola)</strong>. In Google Fogli: <strong>File → Scarica → CSV</strong>. Il file avrà estensione <code>.csv</code>.</div>
                      <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                        💡 Se salvi con Excel italiano, il delimitatore sarà <code>;</code> (punto-e-virgola) — non è un problema, YUP lo riconosce automaticamente.
                      </div>
                    </div>
                  </div>

                  {/* STEP 5 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">5</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Trascina il file nel riquadro qui sotto</div>
                      <div className="text-gray-600 text-xs mt-1">Clicca "Preview" per vedere quante righe verranno create/aggiornate e quali eventualmente scartate. Poi "Applica al database" per completare l'import.</div>
                    </div>
                  </div>

                  {/* STEP 6 */}
                  <div className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0">6</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">Controlla il risultato</div>
                      <div className="text-gray-600 text-xs mt-1">
                        Se ci sono righe scartate, il pannello dei risultati elencherà il motivo esatto (es. "city_slug 'desezano' non esiste"). Correggi il CSV e ricarica: le righe duplicate NON verranno duplicate, ma aggiornate.
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {showSchema && csvSchema && (
              <div className="mt-5 border border-blue-200 rounded-lg overflow-hidden" data-testid="csv-schema-panel">
                <div className="px-4 py-2 bg-blue-50 text-sm font-semibold text-blue-900 border-b border-blue-200">
                  Struttura richiesta del CSV
                </div>
                <div className="p-4 space-y-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Colonne OBBLIGATORIE</div>
                    <div className="flex flex-wrap gap-1.5">
                      {csvSchema.required_columns?.map(c => (
                        <code key={c} className="px-2 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">{c}</code>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Se manca una di queste, la riga viene scartata.</div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Colonne opzionali</div>
                    <div className="flex flex-wrap gap-1.5">
                      {csvSchema.optional_columns?.map(c => (
                        <code key={c} className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700 text-xs">{c}</code>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Alias intestazioni (equivalenti)</div>
                    <div className="text-xs text-gray-600 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                      {Object.entries(csvSchema.supported_header_aliases || {}).map(([canonical, aliases]) => (
                        <div key={canonical}>
                          <code className="font-mono text-blue-700">{canonical}</code>{' = '}
                          <span className="text-gray-500">{aliases.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">
                      Valori <code>city_slug</code> validi ({csvSchema.valid_city_slugs?.length || 0})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {csvSchema.valid_city_slugs?.map(c => (
                        <code key={c.slug} className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs">
                          {c.slug} <span className="text-emerald-600/60">({c.name})</span>
                        </code>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">
                      Valori <code>category_key</code> validi ({csvSchema.valid_category_keys?.length || 0})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {csvSchema.valid_category_keys?.map(c => (
                        <code key={c.key} className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs" title={c.label}>
                          {c.key}
                        </code>
                      ))}
                    </div>
                  </div>

                  {csvSchema.valid_subcategories?.length > 0 && (
                    <details>
                      <summary className="text-xs uppercase tracking-widest text-gray-500 font-semibold cursor-pointer">
                        Sottocategorie disponibili ({csvSchema.valid_subcategories.length}) — clicca per espandere
                      </summary>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {csvSchema.valid_subcategories.map((s, i) => (
                          <code key={`${s.category_key}-${s.key}-${i}`} className="px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-700 text-xs" title={s.label}>
                            {s.category_key}/<span className="text-gray-500">{s.key}</span>
                          </code>
                        ))}
                      </div>
                    </details>
                  )}

                  <div className="text-xs bg-gray-50 border border-gray-200 rounded p-3">
                    <div className="font-semibold text-gray-700 mb-1">Suggerimenti</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-gray-600">
                      <li>Salva il file da Excel come <strong>CSV UTF-8</strong>. Se ci sono caratteri strani, apri il file con Blocco Note e verifica la codifica.</li>
                      <li>Le colonne <code>featured</code> e <code>verified</code> accettano: <code>true</code>, <code>si</code>, <code>1</code>.</li>
                      <li>Se un'attività ha lo stesso <code>name + city + category + address</code> di una già presente, verrà <strong>aggiornata</strong> (non duplicata).</li>
                      <li>Puoi importare in più batch: attività diverse non verranno rimosse.</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
              <input type="file" accept=".csv,.txt" onChange={e => { setCsvFile(e.target.files?.[0]); setCsvPreview(null); setCsvApplyResult(null); }} className="hidden" id="csv-input" data-testid="csv-file-input" />
              <label htmlFor="csv-input" className="cursor-pointer">
                <Upload size={28} className="mx-auto text-gray-400 mb-2" />
                <div className="text-sm text-gray-700 font-medium">{csvFile ? csvFile.name : 'Clicca per selezionare file CSV'}</div>
                <div className="text-xs text-gray-500 mt-1">o trascinalo qui</div>
              </label>
            </div>

            {csvFile && (
              <div className="mt-4 flex gap-3">
                <button onClick={previewCsv} disabled={csvBusy}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="csv-preview-btn">
                  {csvBusy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />} Preview
                </button>
                {csvPreview && (
                  <button onClick={applyCsv} disabled={csvBusy}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="csv-apply-btn">
                    <CheckCircle2 size={16} /> Applica al database
                  </button>
                )}
              </div>
            )}

            {csvPreview && (
              <div className="mt-4">
                <div className="text-sm text-gray-700 mb-2">
                  <strong>{csvPreview.total}</strong> righe valide, <strong>{csvPreview.errors?.length || 0}</strong> errori/warning
                  {csvPreview.delimiter && <span className="ml-2 text-gray-500">(delimitatore: <code>{csvPreview.delimiter === ';' ? 'punto-e-virgola' : 'virgola'}</code>)</span>}
                  {csvPreview.detected_headers?.length > 0 && (
                    <span className="ml-2 text-gray-500">Colonne rilevate: {csvPreview.detected_headers.join(', ')}</span>
                  )}
                </div>
                {csvPreview.errors?.length > 0 && (
                  <ul className="text-xs bg-red-50 border border-red-200 rounded p-3 space-y-1 max-h-40 overflow-auto text-red-700" data-testid="csv-preview-errors">
                    {csvPreview.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
                <div className="mt-3 max-h-72 overflow-auto border border-gray-200 rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
                      <tr><th className="text-left px-3 py-2">Nome</th><th className="text-left px-3 py-2">Città</th><th className="text-left px-3 py-2">Categoria</th><th className="text-left px-3 py-2">Azione</th></tr>
                    </thead>
                    <tbody>
                      {csvPreview.preview.slice(0, 100).map((p, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2">{p.name}</td>
                          <td className="px-3 py-2 text-gray-600">{p.city_slug}</td>
                          <td className="px-3 py-2 text-gray-600">{p.category_key}</td>
                          <td className="px-3 py-2"><ActionPill action={p._action} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {csvJobProgress && (
              <div className="mt-4 p-4 rounded-lg border border-blue-200 bg-blue-50" data-testid="csv-progress-panel">
                <div className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Loader2 size={14} className={csvJobProgress.status === 'completed' ? '' : 'animate-spin'}/>
                  {csvJobProgress.uploading ? `Upload in corso... ${csvJobProgress.uploadPct}%` :
                   csvJobProgress.status === 'queued' ? 'In coda...' :
                   csvJobProgress.status === 'running' ? `Elaborazione: ${csvJobProgress.processedRows}/${csvJobProgress.totalRows} righe` :
                   csvJobProgress.status === 'completed' ? 'Elaborazione completata ✓' :
                   csvJobProgress.status === 'failed' ? 'Import fallito ✗' : csvJobProgress.status}
                </div>
                {csvJobProgress.uploading ? (
                  <div className="w-full bg-blue-100 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${csvJobProgress.uploadPct}%` }} data-testid="csv-upload-progress"/>
                  </div>
                ) : (
                  <>
                    <div className="w-full bg-blue-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${csvJobProgress.totalRows ? Math.round((csvJobProgress.processedRows / csvJobProgress.totalRows) * 100) : 0}%` }}
                        data-testid="csv-processing-progress"
                      />
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div className="text-emerald-700">✓ Create: <strong>{csvJobProgress.created || 0}</strong></div>
                      <div className="text-blue-700">↻ Aggiornate: <strong>{csvJobProgress.updated || 0}</strong></div>
                      <div className="text-amber-700">⚠ Scartate: <strong>{csvJobProgress.skipped || 0}</strong></div>
                    </div>
                  </>
                )}
              </div>
            )}

            {csvApplyResult && (
              <div className={`mt-4 p-4 border rounded ${csvApplyResult.skipped > 0 || csvApplyResult.errors?.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
                   data-testid="csv-apply-result">
                <div className={`font-medium flex items-center gap-2 ${csvApplyResult.skipped > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                  <CheckCircle2 size={18} /> Import completato
                </div>
                <div className={`text-sm mt-1 ${csvApplyResult.skipped > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                  Create: <strong>{csvApplyResult.created}</strong> · Aggiornate: <strong>{csvApplyResult.updated}</strong> · Scartate: <strong>{csvApplyResult.skipped}</strong>
                </div>
                {csvApplyResult.errors?.length > 0 && (
                  <details className="mt-3" open>
                    <summary className="text-sm text-amber-800 font-semibold cursor-pointer">
                      Mostra {csvApplyResult.errors.length} problem{csvApplyResult.errors.length === 1 ? 'a' : 'i'} riscontrati
                    </summary>
                    <ul className="mt-2 text-xs bg-white/70 border border-amber-300 rounded p-3 space-y-1 max-h-64 overflow-auto text-amber-900" data-testid="csv-apply-errors">
                      {csvApplyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- JOBS ---------- */}
      {tab === 'jobs' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Fonte</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Pagine</th>
                <th className="text-left px-4 py-3">Attività</th>
                <th className="text-left px-4 py-3">Immagini</th>
                <th className="text-left px-4 py-3">Avviato</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => { setTab('wix'); setCurrentJob(j); }}>
                  <td className="px-4 py-3 font-mono text-xs">{j.id}</td>
                  <td className="px-4 py-3">{j.source}</td>
                  <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-3">{j.pages_scanned}</td>
                  <td className="px-4 py-3">{j.activities_found}</td>
                  <td className="px-4 py-3">{j.images_downloaded}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{j.started_at ? new Date(j.started_at).toLocaleString('it-IT') : '—'}</td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500 text-sm">Nessun job registrato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, val, color = 'text-gray-800' }) {
  return (
    <div className="bg-gray-50 rounded p-3 text-center">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{val ?? 0}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-gray-100 text-gray-700',
    scanning: 'bg-blue-100 text-blue-700 animate-pulse',
    previewed: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function ActionPill({ action }) {
  const map = {
    create: 'bg-green-100 text-green-700',
    update: 'bg-blue-100 text-blue-700',
    duplicate: 'bg-gray-100 text-gray-500',
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${map[action] || 'bg-gray-100'}`}>{action}</span>;
}
